import { useEffect, useState } from "react";

/**
 * Reactive media query hook — returns `true` when the given CSS media query
 * matches and updates automatically whenever the viewport changes.
 *
 * Prefer this over `window.innerWidth` checks in render code so that
 * components re-render correctly on resize without manual event listeners.
 *
 * @param {string} query - A valid CSS media query string.
 * @returns {boolean} Whether the media query currently matches.
 *
 * @example
 * const isMobile  = useMediaQuery("(max-width: 767px)");
 * const isTablet  = useMediaQuery("(min-width: 768px) and (max-width: 1023px)");
 * const isDesktop = useMediaQuery("(min-width: 1024px)");
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => window.matchMedia(query).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);

  return matches;
}
